import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip, Checkbox, ListItemText } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import { FILTERS_UPDATE } from '../../actions/siteActions';

const ActivityTitleFilter = ({ sites }) => {
  const dispatch  = useDispatch();
  const filters   = useSelector((s) => s.sites.filters);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Collect unique titles from visible active sites
  const availableTitles = useMemo(() => {
    const set = new Set();
    sites.forEach(function(s) {
      if (s._title && s._status !== 'none') set.add(s._title);
    });
    return Array.from(set).sort();
  }, [sites]);

  const selected = filters.selectedTitles || [];

  const toggle = (title) => {
    const next = selected.includes(title)
      ? selected.filter((t) => t !== title)
      : [...selected, title];
    dispatch({ type: FILTERS_UPDATE, payload: { selectedTitles: next } });
  };

  const clear = () => {
    dispatch({ type: FILTERS_UPDATE, payload: { selectedTitles: [] } });
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (availableTitles.length === 0) return null;

  return (
    <Box ref={ref} sx={{ position: 'absolute', top: 56, right: 16, zIndex: 1000 }}>

      {/* Trigger button */}
      <Box onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          backgroundColor: selected.length > 0 ? '#1565C0' : '#0D1B2A',
          color: '#fff', borderRadius: 2, px: 2, py: 0.9, cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', border: '1px solid #1E3A5F',
          transition: 'all 0.15s',
          '&:hover': { backgroundColor: '#1565C0', borderColor: '#1565C0' },
        }}>
        <WorkIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#fff', fontFamily: 'inherit' }}>
          Activity
        </Typography>
        {selected.length > 0 && (
          <Chip label={selected.length} size="small"
            sx={{ ml: 0.5, backgroundColor: '#FF6B35', color: '#fff', height: 18, fontSize: 10 }} />
        )}
      </Box>

      {/* Dropdown */}
      {open && (
        <Box sx={{
          position: 'absolute', top: '100%', right: 0, mt: 0.5,
          backgroundColor: '#0D1B2A', border: '1px solid #1E3A5F',
          borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          minWidth: 260, maxWidth: 380, maxHeight: 400, overflowY: 'auto',
          zIndex: 1400,
        }}>

          {/* Header */}
          <Box sx={{
            px: 2, py: 1.2, borderBottom: '1px solid #1E3A5F',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            position: 'sticky', top: 0, backgroundColor: '#0D1B2A',
          }}>
            <Typography sx={{ color: '#90A4AE', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Activity Titles ({availableTitles.length})
            </Typography>
            {selected.length > 0 && (
              <Typography onClick={clear}
                sx={{ color: '#FF6B35', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                      '&:hover': { color: '#fff' } }}>
                Clear all
              </Typography>
            )}
          </Box>

          {/* Options */}
          {availableTitles.map((title) => {
            const active = selected.includes(title);
            return (
              <Box key={title} onClick={() => toggle(title)}
                sx={{
                  display: 'flex', alignItems: 'center', px: 1.5, py: 0.5,
                  cursor: 'pointer', borderBottom: '1px solid #0D2A40',
                  '&:hover': { backgroundColor: '#0D2A40' },
                }}>
                <Checkbox size="small" checked={active}
                  sx={{ p: 0.5, color: '#1E3A5F', '&.Mui-checked': { color: '#4FC3F7' } }} />
                <ListItemText
                  primary={title}
                  primaryTypographyProps={{
                    fontSize: 12, color: active ? '#E0E7FF' : '#90A4AE',
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontWeight: active ? 700 : 400,
                  }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ActivityTitleFilter;